import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import styled from 'styled-components';
import {useStoreContext} from '@store/store-context';
import {Button} from '@ui/atoms/button';
import {MyObjectivesPage} from './my-objective/myObjective';
import {MyGoalsPage} from './my-objective/my-goals-v2';
import {PageHeading} from './my-objective/my-goals-v2.styles';
import {VerticalSpacer} from '@ui/atoms/spacer';
import {KpisPage} from './kpis/kpis-page';
import {GoalsDirectReports} from './direct-reports/direct-reports';
import {AllGoalsPage} from './all-goals/all-goals-page';
import {GoalViewNavigation} from './goal-view-navigation';
import {AlignmentGoalsPage} from './alignment/alignment-page';
import {
  FeaturePageContent,
  FeaturePageHeader,
} from '@ui/molecules/feature-page-header';

const Wrapper = styled.div`
  width: 100%;
  min-height: 100vh;
`;

type GoalWorkspaceView = 'my' | 'team' | 'all';

export const ObjectivesPage = observer(() => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    storeDashboardRoutes: {ObjTabs, GoalViewTabs},
    authStore: {auth},
    usersStore: {users},
    goalsState,
  } = useStoreContext();

  const requestedView = searchParams.get('view');
  const selectedView: GoalWorkspaceView =
    requestedView === 'team' || requestedView === 'all' ? requestedView : 'my';
  const hasDirectReports = useMemo(
    () =>
      users.some((user: any) => {
        const reviewerId =
          typeof user.reviewer === 'string' ? user.reviewer : user.reviewer?.id;
        return reviewerId === auth.user.id;
      }),
    [auth.user.id, users],
  );

  const selectView = useCallback(
    (view: GoalWorkspaceView) => {
      const next = new URLSearchParams(searchParams);
      if (view === 'my') next.delete('view');
      else next.set('view', view);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (selectedView === 'team' && users.length > 0 && !hasDirectReports) {
      selectView('my');
    }
  }, [hasDirectReports, selectView, selectedView, users.length]);

  const handleCreateGoalClick = useCallback(() => {
    goalsState.setShowGoalForm(true);
    goalsState.setEditing(false);
    navigate('/create-goal', {state: undefined});
  }, [goalsState, navigate]);

  const CreateGoalElement = () => (
    <Button
      kind="primary"
      style={{height: '46px', padding: '12px 24px'}}
      onClick={handleCreateGoalClick}>
      Create a goal
    </Button>
  );

  const showRevampedMyGoals = selectedView === 'my' && GoalViewTabs.flat;
  const showAlignment = GoalViewTabs.tree;
  const pageTitle =
    selectedView === 'my'
      ? 'My Goals'
      : selectedView === 'team'
      ? 'Team Goals'
      : 'All Goals';
  const pageSubtitle =
    selectedView === 'my'
      ? 'Manage personal, team, and company-wide objectives, KPIs, and progress.'
      : selectedView === 'team'
      ? 'Track your team’s goals, progress, and where support is needed.'
      : 'Explore goals and progress across the company and every team.';

  return (
    <Wrapper>
      <FeaturePageHeader
        ariaLabel="Goal views"
        width="wide"
        tabs={[
          {
            id: 'my',
            label: 'My Goals',
            active: selectedView === 'my',
            onClick: () => selectView('my'),
          },
          {
            id: 'team',
            label: 'Team Goals',
            active: selectedView === 'team',
            hidden: !hasDirectReports,
            onClick: () => selectView('team'),
          },
          {
            id: 'all',
            label: 'All Goals',
            active: selectedView === 'all',
            onClick: () => selectView('all'),
          },
        ]}
        actions={<GoalViewNavigation />}
      />

      <FeaturePageContent width="wide">
        {showRevampedMyGoals ? (
          <MyGoalsPage onCreateGoal={handleCreateGoalClick} />
        ) : (
          <>
            <PageHeading>
              <div>
                <h1>{pageTitle}</h1>
                <p>{pageSubtitle}</p>
              </div>
              {selectedView !== 'team' && <CreateGoalElement />}
            </PageHeading>

            {showAlignment ? (
              <AlignmentGoalsPage scope={selectedView} />
            ) : (
              <>
                {ObjTabs.objective && selectedView === 'my' && (
                  <MyObjectivesPage />
                )}
                {ObjTabs.objective &&
                  selectedView === 'team' &&
                  hasDirectReports && <GoalsDirectReports scope="team" />}
                {ObjTabs.objective && selectedView === 'all' && (
                  <AllGoalsPage />
                )}
                {ObjTabs.kpi && <KpisPage />}
              </>
            )}
          </>
        )}
        <VerticalSpacer size="200px" />
      </FeaturePageContent>
    </Wrapper>
  );
});
